import { useMemo } from "react";
import { AlertTriangle, Clock, CheckCircle2, CalendarClock, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const AZUL_MAX = 15;
const AMARELO_MAX = 30;

const daysBetween = (isoA, isoB) => {
  const a = new Date(isoA).getTime();
  const b = new Date(isoB).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  return Math.floor((b - a) / (1000 * 60 * 60 * 24));
};

const classify = (prospecto, hoje) => {
  const created = prospecto.created_at || "";
  const diasEspera = daysBetween(created, hoje) ?? 0;
  const dataPrev = prospecto.data_aprovacao_prevista || "";
  const diasAtePrevisao = dataPrev ? daysBetween(hoje, dataPrev) : null;

  let level = "azul";
  if (diasEspera > AMARELO_MAX) level = "vermelho";
  else if (diasEspera > AZUL_MAX) level = "amarelo";
  // se estiver a 7 dias ou menos da aprovação prevista, promove pelo menos p/ amarelo
  if (diasAtePrevisao !== null && diasAtePrevisao <= 7 && diasAtePrevisao >= 0 && level === "azul") {
    level = "amarelo";
  }
  // se a data prevista já venceu, promove p/ vermelho
  if (diasAtePrevisao !== null && diasAtePrevisao < 0) level = "vermelho";

  return { level, diasEspera, diasAtePrevisao };
};

const LEVEL_STYLE = {
  azul: {
    label: "No prazo",
    border: "border-sky-500/40",
    bg: "bg-sky-500/5",
    text: "text-sky-500",
    dot: "bg-sky-500",
    icon: CheckCircle2,
  },
  amarelo: {
    label: "Atenção",
    border: "border-amber-500/50",
    bg: "bg-amber-500/5",
    text: "text-amber-500",
    dot: "bg-amber-500",
    icon: Clock,
  },
  vermelho: {
    label: "Crítico",
    border: "border-red-500/50",
    bg: "bg-red-500/10",
    text: "text-red-500",
    dot: "bg-red-500",
    icon: AlertTriangle,
  },
};

export default function AlertasInfraestrutura({ condominios = [] }) {
  const hojeISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const grupos = useMemo(() => {
    const prospectos = condominios.filter((c) => c.infra_status === "prospecto");
    const azul = [], amarelo = [], vermelho = [];
    for (const p of prospectos) {
      const info = classify(p, hojeISO);
      const item = { ...p, ...info };
      if (info.level === "vermelho") vermelho.push(item);
      else if (info.level === "amarelo") amarelo.push(item);
      else azul.push(item);
    }
    // ordena cada grupo por dias aguardando desc
    const byWait = (a, b) => (b.diasEspera || 0) - (a.diasEspera || 0);
    return { azul: azul.sort(byWait), amarelo: amarelo.sort(byWait), vermelho: vermelho.sort(byWait) };
  }, [condominios, hojeISO]);

  const total = grupos.azul.length + grupos.amarelo.length + grupos.vermelho.length;

  if (total === 0) return null;

  const renderItem = (item) => {
    const st = LEVEL_STYLE[item.level];
    const Icon = st.icon;
    const dataPrevTxt = item.data_aprovacao_prevista || "sem previsão";
    const legenda =
      item.diasAtePrevisao !== null && item.diasAtePrevisao < 0
        ? `${Math.abs(item.diasAtePrevisao)}d em atraso`
        : item.diasAtePrevisao !== null && item.diasAtePrevisao <= 7
        ? `${item.diasAtePrevisao}d p/ aprovação`
        : `${item.diasEspera}d aguardando`;
    return (
      <div
        key={item.id}
        className={`rounded-lg border ${st.border} ${st.bg} p-3 flex items-start gap-3`}
        data-testid={`alerta-infra-${item.level}-${item.id}`}
      >
        <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${st.text}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{item.condominio}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
            <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {dataPrevTxt}</span>
            {item.responsavel && (
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {item.responsavel}</span>
            )}
          </div>
        </div>
        <Badge variant="outline" className={`${st.text} border-current shrink-0 tabular-nums`}>
          {legenda}
        </Badge>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="alertas-infraestrutura">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Alertas de infraestrutura</h3>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className="text-sky-500 border-sky-500/40" data-testid="badge-count-azul">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 mr-1.5" />
            {grupos.azul.length} no prazo
          </Badge>
          <Badge variant="outline" className="text-amber-500 border-amber-500/40" data-testid="badge-count-amarelo">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5" />
            {grupos.amarelo.length} atenção
          </Badge>
          <Badge variant="outline" className="text-red-500 border-red-500/40" data-testid="badge-count-vermelho">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5" />
            {grupos.vermelho.length} crítico
          </Badge>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3">
        <span className="text-sky-500 font-semibold">Azul</span>: aguardando ≤ {AZUL_MAX} dias.{" "}
        <span className="text-amber-500 font-semibold">Amarelo</span>: {AZUL_MAX + 1}–{AMARELO_MAX} dias
        (ou aprovação prevista em até 7 dias).{" "}
        <span className="text-red-500 font-semibold">Vermelho</span>: acima de {AMARELO_MAX} dias — hora de cobrar o provedor.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { key: "vermelho", list: grupos.vermelho, title: "Crítico (> 30 dias)" },
          { key: "amarelo", list: grupos.amarelo, title: "Atenção (16–30 dias)" },
          { key: "azul", list: grupos.azul, title: "No prazo (≤ 15 dias)" },
        ].map(({ key, list, title }) => (
          <div key={key} className="space-y-2" data-testid={`alerta-col-${key}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${LEVEL_STYLE[key].dot}`} />
              {title}
            </div>
            {list.length === 0 ? (
              <div className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3 text-center">
                Nenhum prospecto neste nível.
              </div>
            ) : (
              list.map(renderItem)
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
